// src/ApiPanel/components/reports/InvoiceReport.jsx
// The engine behind all six invoice screens (Sell, Sell CMS, Sell Recharge,
// Purchase, Purchase Recharge, Purchase Utility). They differ only in which
// endpoint they read and what they are called, so they share one component
// rather than six near-identical files that drift apart.
//
// Includes the rate-wise summary the API returns, because that — taxable
// value and tax split by GST rate — is the shape a GSTR-1 filing actually
// needs, and having to pivot it by hand in Excel is how mistakes get in.
import React, { useCallback, useState } from "react";
import {
  Badge, Card, DataTable, DateField, ExportButton, FilterBar, Modal, Notice,
  Pagination, ReportPage, SelectField, Stat, StatGrid, TextField,
  daysAgoISO, money, prettyDate, rupees, todayISO, count,
} from "./ReportKit";
import { useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, apiPanelFetch, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";

const STATUS_OPTIONS = [
  { value: "ISSUED", label: "Issued" },
  { value: "CANCELLED", label: "Cancelled" },
];

const defaultFilters = () => ({
  start_date: daysAgoISO(29),
  end_date: todayISO(),
  gstin: "",
  invoice_no: "",
  invoice_status: "",
  page: 1,
  page_size: 50,
});

export default function InvoiceReport({ title, subtitle, endpoint, exportName }) {
  const [filters, setFilters] = useState(defaultFilters);
  const [applied, setApplied] = useState(defaultFilters);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");

  const { data, loading, error, reload } = useReportQuery(endpoint, applied);

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const apply = () => setApplied({ ...filters, page: 1 });
  const reset = () => {
    const fresh = defaultFilters();
    setFilters(fresh);
    setApplied(fresh);
  };
  const goPage = (page) => setApplied((a) => ({ ...a, page }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${endpoint}${qs({ ...applied, export: "csv", page: undefined, page_size: undefined })}`,
        `${exportName}_${applied.start_date}_${applied.end_date}.csv`
      );
    } catch {
      /* the download failing is visible to the user as nothing happening;
         the report itself is unaffected, so don't blow away the page state */
    } finally {
      setExporting(false);
    }
  };

  const openDetail = useCallback(async (row) => {
    setDetail({ loading: true });
    setDetailError("");
    try {
      const res = await apiPanelFetch(
        `${API_PANEL_ENDPOINTS.CA_INVOICE_DETAIL}${qs({ id: row.id })}`
      );
      setDetail({ loading: false, ...res });
    } catch (err) {
      setDetail(null);
      setDetailError(err.message || "Could not open that invoice.");
    }
  }, []);

  const totals = data?.totals || {};
  const rows = data?.data || [];

  const columns = [
    {
      key: "invoice_no",
      label: "Invoice No",
      render: (r) => <span className="apip-mono">{r.invoice_no}</span>,
    },
    { key: "invoice_date", label: "Date", render: (r) => prettyDate(r.invoice_date) },
    { key: "supplier_name", label: "Supplier", render: (r) => partyCell(r.supplier_name, r.supplier_gstin) },
    { key: "recipient_name", label: "Recipient", render: (r) => partyCell(r.recipient_name, r.recipient_gstin) },
    {
      key: "supply_type",
      label: "Supply",
      render: (r) => (
        <span className="apip-supply">
          {r.supply_type}
          <small>{r.place_of_supply_name || r.place_of_supply}</small>
        </span>
      ),
    },
    { key: "taxable_value", label: "Taxable", numeric: true, render: (r) => money(r.taxable_value) },
    { key: "cgst", label: "CGST", numeric: true, render: (r) => money(r.cgst) },
    { key: "sgst", label: "SGST", numeric: true, render: (r) => money(r.sgst) },
    { key: "igst", label: "IGST", numeric: true, render: (r) => money(r.igst) },
    {
      key: "invoice_total",
      label: "Total",
      numeric: true,
      render: (r) => <strong>{money(r.invoice_total)}</strong>,
    },
    { key: "status", label: "Status", render: (r) => <Badge value={r.status} /> },
  ];

  const footer = [
    { value: "Totals" }, "", "", "", "",
    { value: money(totals.taxable_value), numeric: true },
    { value: money(totals.cgst), numeric: true },
    { value: money(totals.sgst), numeric: true },
    { value: money(totals.igst), numeric: true },
    { value: money(totals.invoice_total), numeric: true },
    "",
  ];

  return (
    <ReportPage
      title={title}
      subtitle={subtitle}
      actions={<ExportButton onExport={handleExport} busy={exporting} />}
    >
      <Card>
        <FilterBar onApply={apply} onReset={reset} busy={loading}>
          <DateField label="From" value={filters.start_date} onChange={set("start_date")} max={filters.end_date} />
          <DateField label="To" value={filters.end_date} onChange={set("end_date")} min={filters.start_date} max={todayISO()} />
          <TextField
            label="GSTIN"
            value={filters.gstin}
            onChange={(v) => set("gstin")(v.toUpperCase())}
            placeholder="27AAAAA0000A1Z5"
            maxLength={15}
          />
          <TextField label="Invoice No" value={filters.invoice_no} onChange={set("invoice_no")} placeholder="Contains…" maxLength={32} />
          <SelectField label="Status" value={filters.invoice_status} onChange={set("invoice_status")} options={STATUS_OPTIONS} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Invoices" value={count(totals.invoice_count)} />
        <Stat label="Taxable Value" value={rupees(totals.taxable_value)} />
        <Stat label="CGST" value={rupees(totals.cgst)} />
        <Stat label="SGST" value={rupees(totals.sgst)} />
        <Stat label="IGST" value={rupees(totals.igst)} />
        <Stat label="Invoice Total" value={rupees(totals.invoice_total)} tone="brand" />
      </StatGrid>

      {detailError && <Notice tone="bad">{detailError}</Notice>}

      {data?.rate_summary?.length > 0 && (
        <Card title="Rate-wise summary" right={<span className="apip-hint">The breakdown GSTR-1 asks for</span>}>
          <DataTable
            columns={[
              { key: "gst_rate", label: "GST Rate", render: (r) => `${Number(r.gst_rate).toFixed(2)}%` },
              { key: "taxable_value", label: "Taxable Value", numeric: true, render: (r) => money(r.taxable_value) },
              { key: "cgst", label: "CGST", numeric: true, render: (r) => money(r.cgst) },
              { key: "sgst", label: "SGST", numeric: true, render: (r) => money(r.sgst) },
              { key: "igst", label: "IGST", numeric: true, render: (r) => money(r.igst) },
            ]}
            rows={data.rate_summary}
            rowKey={(r) => r.gst_rate}
            empty="No rated lines in this period."
          />
        </Card>
      )}

      <Card title="Invoices" right={<span className="apip-hint">Select a row for the full document</span>}>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          empty="No invoices for these filters. If the register was never generated for this period, run generate_ca_invoices first."
          footer={rows.length ? footer : null}
          onRowClick={openDetail}
          rowKey={(r) => r.id}
        />
        <Pagination meta={data?.pagination} onPage={goPage} />
      </Card>

      <InvoiceDetailModal detail={detail} onClose={() => setDetail(null)} onRetry={reload} />
    </ReportPage>
  );
}

function partyCell(name, gstin) {
  return (
    <span className="apip-party">
      {name || "—"}
      {gstin ? <small className="apip-mono">{gstin}</small> : <small className="apip-muted-txt">Unregistered</small>}
    </span>
  );
}

function InvoiceDetailModal({ detail, onClose }) {
  const open = !!detail;
  const inv = detail?.data;

  return (
    <Modal open={open} title={inv ? `Invoice ${inv.invoice_no}` : "Invoice"} onClose={onClose} wide>
      {detail?.loading && <div className="apip-loading">Loading…</div>}

      {inv && (
        <>
          {detail.integrity_ok === false && (
            <Notice tone="bad">
              This invoice&rsquo;s stored checksum does not match its contents. That means the row
              was changed outside the application. Do not rely on these figures — raise it with
              whoever administers the database.
            </Notice>
          )}

          <div className="apip-inv-parties">
            <div>
              <h4>Supplier</h4>
              <p className="apip-inv-name">{inv.supplier_name}</p>
              <p className="apip-mono">{inv.supplier_gstin || "Unregistered"}</p>
              <p className="apip-muted-txt">{inv.supplier_address || "—"}</p>
            </div>
            <div>
              <h4>Recipient</h4>
              <p className="apip-inv-name">{inv.recipient_name}</p>
              <p className="apip-mono">{inv.recipient_gstin || "Unregistered"}</p>
              <p className="apip-muted-txt">{inv.recipient_address || "—"}</p>
            </div>
            <div>
              <h4>Document</h4>
              <dl className="apip-kv">
                <dt>Date</dt><dd>{prettyDate(inv.invoice_date)}</dd>
                <dt>Type</dt><dd>{inv.doc_type}</dd>
                <dt>Module</dt><dd>{inv.module}</dd>
                <dt>Place of supply</dt><dd>{inv.place_of_supply_name || inv.place_of_supply || "—"}</dd>
                <dt>Supply</dt><dd>{inv.supply_type}</dd>
                <dt>Reverse charge</dt><dd>{inv.reverse_charge ? "Yes" : "No"}</dd>
                <dt>Transactions</dt><dd>{count(inv.txn_count)}</dd>
                <dt>Status</dt><dd><Badge value={inv.status} /></dd>
              </dl>
            </div>
          </div>

          {inv.period_from && (
            <p className="apip-hint">
              Consolidates {prettyDate(inv.period_from)} – {prettyDate(inv.period_to)}
            </p>
          )}

          <DataTable
            columns={[
              { key: "line_no", label: "#", width: 44 },
              { key: "description", label: "Description" },
              { key: "hsn_sac", label: "HSN/SAC", render: (l) => <span className="apip-mono">{l.hsn_sac || "—"}</span> },
              { key: "quantity", label: "Qty", numeric: true },
              { key: "taxable_value", label: "Taxable", numeric: true, render: (l) => money(l.taxable_value) },
              { key: "gst_rate", label: "Rate", numeric: true, render: (l) => `${Number(l.gst_rate).toFixed(2)}%` },
              { key: "cgst", label: "CGST", numeric: true, render: (l) => money(l.cgst) },
              { key: "sgst", label: "SGST", numeric: true, render: (l) => money(l.sgst) },
              { key: "igst", label: "IGST", numeric: true, render: (l) => money(l.igst) },
              { key: "line_total", label: "Total", numeric: true, render: (l) => money(l.line_total) },
            ]}
            rows={inv.lines || []}
            rowKey={(l) => l.line_no}
            empty="This invoice has no lines."
          />

          <div className="apip-inv-totals">
            <Row label="Taxable value" value={rupees(inv.taxable_value)} />
            <Row label="CGST" value={rupees(inv.cgst)} />
            <Row label="SGST" value={rupees(inv.sgst)} />
            <Row label="IGST" value={rupees(inv.igst)} />
            {Number(inv.cess) > 0 && <Row label="Cess" value={rupees(inv.cess)} />}
            <Row label="Invoice total" value={rupees(inv.invoice_total)} strong />
            {Number(inv.tds) > 0 && (
              <>
                <Row label="Less: TDS" value={`− ${rupees(inv.tds)}`} />
                <Row label="Net payable" value={rupees(inv.net_payable)} strong />
              </>
            )}
          </div>

          {inv.status === "CANCELLED" && inv.cancel_reason && (
            <Notice tone="warn">Cancelled: {inv.cancel_reason}</Notice>
          )}
        </>
      )}
    </Modal>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className={`apip-inv-total-row${strong ? " strong" : ""}`}>
      <span>{label}</span>
      <span className="apip-num">{value}</span>
    </div>
  );
}
